/**
 * Assignment Activity Modal - Refactored Version
 * Base modal component that orchestrates tab components
 */

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Tabs,
  Tab,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAssignmentDetailV4,
  fetchAssignmentUserProgressV4,
  fetchLinkedServices,
  selectActiveAssignmentDetailV4,
  selectActiveAssignmentDetailV4Status,
  selectAssignmentUserProgressV4,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import type { AssignmentEventData } from '../../types'

// Tab Components
import TabPanel from './assignment-modal/tab-panel'
import OverviewTab from './assignment-modal/overview-tab'
import CoursesTab from './assignment-modal/courses-tab'
import UsersTab from './assignment-modal/users-tab'
import ProgressMatrixTab from './assignment-modal/progress-matrix-tab'

dayjs.extend(relativeTime)

interface AssignmentActivityModalProps {
  event: AssignmentEventData | null
  open: boolean
  onClose: () => void
}

export default function AssignmentActivityModal({
  event,
  open,
  onClose,
}: AssignmentActivityModalProps) {
  let dispatch = useAppDispatch()

  // Tab state
  let [activeTab, setActiveTab] = useState(0)

  // Redux selectors
  let assignment = useAppSelector(selectActiveAssignmentDetailV4)
  let assignmentDetailStatus = useAppSelector(
    selectActiveAssignmentDetailV4Status,
  )
  let userProgress = useAppSelector(selectAssignmentUserProgressV4)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  // Fetch assignment details when modal opens
  useEffect(() => {
    if (
      open &&
      event?.assignmentID &&
      (assignmentDetailStatus === 'uninitialized' ||
        assignment?.id !== event.assignmentID)
    ) {
      dispatch(fetchAssignmentDetailV4(event.assignmentID))
    }
  }, [
    open,
    event?.assignmentID,
    assignmentDetailStatus,
    assignment?.id,
    dispatch,
  ])

  // Fetch linked services for display
  useEffect(() => {
    if (open && linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [open, linkedServicesStatus, dispatch])

  // Fetch user progress when Users tab is opened
  useEffect(() => {
    if (open && activeTab === 2 && event?.assignmentID) {
      dispatch(
        fetchAssignmentUserProgressV4({
          assignmentId: event.assignmentID,
          page: 1,
          pageSize: 100,
        }),
      )
    }
  }, [open, activeTab, event?.assignmentID, dispatch])

  if (!event) {
    return null
  }

  // Derived loading state
  let isLoading =
    assignmentDetailStatus === 'pending' ||
    assignmentDetailStatus === 'uninitialized'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '80vw',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">
              {assignment?.name || 'Assignment Activity'}
            </Typography>
            <Chip
              label={event.activityType
                .replace(/-/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
              sx={{
                bgcolor: 'entityColors.assignment',
                color: 'entityColors.assignment',
                fontWeight: 500,
                '& .MuiChip-label': {
                  color: 'black', // Dark text on yellow background for readability
                },
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {/* Event Metadata */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {dayjs(event.createdDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
            {dayjs(event.createdDate).fromNow()}
          </Typography>
          {event.description && (
            <Typography variant="body2">{event.description}</Typography>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab label="Overview" />
            <Tab label={`Courses (${assignment?.courses?.length || 0})`} />
            <Tab label={`Users (${assignment?.users?.length || 0})`} />
            <Tab label="Progress Matrix" />
          </Tabs>
        </Box>

        {isLoading ? (
          <Box sx={{ py: 2 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={300}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        ) : assignment ? (
          <>
            {/* Tab 0: Assignment Overview */}
            <TabPanel value={activeTab} index={0}>
              <OverviewTab assignment={assignment} event={event} />
            </TabPanel>

            {/* Tab 1: Courses */}
            <TabPanel value={activeTab} index={1}>
              <CoursesTab assignment={assignment} />
            </TabPanel>

            {/* Tab 2: Users */}
            <TabPanel value={activeTab} index={2}>
              <UsersTab userProgress={userProgress} />
            </TabPanel>

            {/* Tab 3: Progress Matrix */}
            <TabPanel value={activeTab} index={3}>
              <ProgressMatrixTab
                assignment={assignment}
                userProgress={userProgress}
                event={event}
                isActive={activeTab === 3}
              />
            </TabPanel>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Assignment details could not be loaded.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
